import { ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDeskSummary } from '@/hooks/useDeskSummary'
import { accessOf } from '@/lib/admin-access'
import { activeNavTo, visibleNav, type DeskCounts, type NavGroup } from '@/lib/nav'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { NAV_ICONS } from './nav-icons'
import ProfileMenu from './ProfileMenu'

interface SidebarProps {
  variant?: 'docked' | 'drawer'
  /** Drawer only: called after any row click or the close button, so the sheet closes. */
  onNavigate?: () => void
}

// Focus ring inset: the nav scrolls, and an outside outline would be clipped at the edges.
const ROW =
  'flex h-10 items-center gap-3 rounded-md px-3 text-sm text-sidebar-foreground outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-sidebar-ring max-sm:min-h-11'
const ICON_BTN =
  'grid size-8 shrink-0 place-items-center rounded-md text-sidebar-label outline-none hover:bg-sidebar-hover hover:text-sidebar-foreground focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-sidebar-ring max-sm:size-11'

/** Open items in this queue; hidden at zero. The rail has no room beside the icon, so it gets a corner badge. */
function Count({ n, rail }: { n?: number; rail: boolean }) {
  if (!n) return null
  const text = n > 99 ? '99+' : n
  return rail ? (
    <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-sidebar-primary px-1 text-center text-[11px] leading-4 font-bold text-sidebar-primary-foreground tabular-nums">
      {text}
    </span>
  ) : (
    <span className="ml-auto text-[13px] font-semibold text-sidebar-primary tabular-nums">{text}</span>
  )
}

function Row({
  to,
  label,
  count,
  active,
  rail,
  icon,
  className,
  onNavigate,
}: {
  to: string
  label: string
  count?: number
  active: boolean
  rail: boolean
  icon?: ReactNode
  className?: string
  onNavigate?: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      aria-label={count ? `${label}, ${count} open` : rail ? label : undefined}
      title={rail ? label : undefined}
      className={cn(ROW, active ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground' : 'hover:bg-sidebar-hover', rail && 'relative justify-center px-0', className)}
    >
      {icon}
      {rail ? null : <span className="truncate">{label}</span>}
      <Count n={count} rail={rail} />
    </Link>
  )
}

function Group({
  group,
  folded,
  onToggle,
  rail,
  active,
  count,
  onNavigate,
}: {
  group: NavGroup
  folded: boolean
  onToggle: () => void
  rail: boolean
  active: string | null
  count: (fn?: (c: DeskCounts) => number) => number | undefined
  onNavigate?: () => void
}) {
  const listId = `nav-${group.id}`
  const hasActive = group.items.some((i) => i.path === active || i.children?.some((c) => c.to === active))
  const hidden = !rail && folded
  return (
    <div className="mt-2 first:mt-0">
      {group.heading && !rail ? (
        <button
          type="button"
          aria-expanded={!folded}
          aria-controls={listId}
          onClick={onToggle}
          className={cn(
            'flex h-8 w-full items-center gap-1 rounded-md px-3 text-xs font-semibold outline-none hover:bg-sidebar-hover focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-sidebar-ring max-sm:h-11',
            // A folded section still tells you where you are.
            folded && hasActive ? 'text-sidebar-accent-foreground' : 'text-sidebar-label',
          )}
        >
          {group.heading}
          {folded ? <ChevronRight className="size-3.5" aria-hidden /> : <ChevronDown className="size-3.5" aria-hidden />}
        </button>
      ) : null}
      {group.heading && rail ? <div className="mx-2 mb-2 border-t border-sidebar-border" /> : null}
      <ul id={listId} hidden={hidden} className="space-y-0.5">
        {group.items.map((item) => {
          const Icon = NAV_ICONS[item.icon]
          return (
            <li key={item.path}>
              <Row to={item.to ?? item.path} label={item.label} count={count(item.count)} active={active === item.path} rail={rail} icon={<Icon className="size-5 shrink-0" aria-hidden />} onNavigate={onNavigate} />
              {item.children && !rail ? (
                <ul className="nav-tree mt-0.5">
                  {item.children.map((child) => (
                    <li key={child.to}>
                      <Row to={child.to} label={child.label} count={count(child.count)} active={active === child.to} rail={false} className="h-9" onNavigate={onNavigate} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default function Sidebar({ variant = 'docked', onNavigate }: SidebarProps) {
  const { sidebarOpen, toggleSidebar, collapsedGroups, toggleGroup } = useUIStore()
  const role = accessOf(useAuthStore((s) => s.user))
  const summary = useDeskSummary().data
  const { pathname, search } = useLocation()
  const isDrawer = variant === 'drawer'
  const rail = !isDrawer && !sidebarOpen
  const groups = visibleNav(role)
  const active = activeNavTo(groups, pathname, search)
  const count = (fn?: (c: DeskCounts) => number) => (summary && fn ? fn(summary) : undefined)

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar',
        isDrawer
          ? 'relative w-full'
          : cn('fixed top-0 left-0 z-30 hidden h-full transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex', rail ? 'w-16' : 'w-70'),
      )}
    >
      <div className={cn('flex shrink-0 items-center gap-2.5', rail ? 'flex-col py-3' : 'h-14 pr-2 pl-4')}>
        <img src="/apple-touch-icon.png" alt="" className="size-7 shrink-0 rounded-md" />
        {rail ? null : <span className="text-[17px] leading-6 font-bold text-sidebar-foreground">EsaFX</span>}
        {isDrawer ? (
          <button type="button" onClick={onNavigate} aria-label="Close menu" className={cn(ICON_BTN, 'ml-auto')}>
            <X className="size-4" aria-hidden />
          </button>
        ) : (
          <button type="button" onClick={toggleSidebar} aria-label={rail ? 'Expand menu' : 'Collapse menu'} aria-expanded={!rail} className={cn(ICON_BTN, !rail && 'ml-auto')}>
            {rail ? <ChevronsRight className="size-4" aria-hidden /> : <ChevronsLeft className="size-4" aria-hidden />}
          </button>
        )}
      </div>

      <nav aria-label="Main" className="min-h-0 flex-1 overflow-y-auto px-3 py-2 [scrollbar-color:var(--sidebar-guide)_transparent] [scrollbar-width:thin]">
        {groups.map((group) => (
          <Group key={group.id} group={group} folded={!!collapsedGroups[group.id]} onToggle={() => toggleGroup(group.id)} rail={rail} active={active} count={count} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="shrink-0 border-t border-sidebar-border p-2">
        <ProfileMenu compact={rail} />
      </div>
    </aside>
  )
}
